// Global Instructions Rule Applied!
// Read shapes for the fund Ledger tab: trial-balance rows
// (view__fund_trial_balance) and GL drill-down rows (view__fund_gl_detail).
// Balances are debit-positive signed sums; _fmt twins are server-formatted.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

export const FundTrialBalanceRowSchema = z
  .object({
    acc_chart_of_accounts__id: z.string(),
    acc_gl_accounts__account_code: z.string().nullish(),
    acc_gl_accounts__account_name: z.string(),
    cfg_acc_account_types__label: z.string().nullish(),
    prog__opening: z.number(),
    prog__opening_fmt: z.string().nullish(),
    prog__debits: z.number(),
    prog__debits_fmt: z.string().nullish(),
    prog__credits: z.number(),
    prog__credits_fmt: z.string().nullish(),
    prog__closing: z.number(),
    prog__closing_fmt: z.string().nullish()
  })
  .strict()

export type FundTrialBalanceRow = z.infer<typeof FundTrialBalanceRowSchema>

export const FundLedgerFinYearSchema = z
  .object({
    id: z.string(),
    fin_year: z.number(),
    year_start_date: z.string().nullish(),
    year_end_date: z.string().nullish(),
    closed: z.boolean().nullish(),
    window_end: z.string().nullish()
  })
  .passthrough()

export type FundLedgerFinYear = z.infer<typeof FundLedgerFinYearSchema>

export const FundLedgerTotalsSchema = z
  .object({
    opening: z.number(),
    debits: z.number(),
    credits: z.number(),
    closing: z.number(),
    balanced: z.boolean()
  })
  .passthrough()

export type FundLedgerTotals = z.infer<typeof FundLedgerTotalsSchema>

export function validateFundTrialBalanceRows(data: unknown): FundTrialBalanceRow[] {
  try {
    return validateWithZodSchema(FundTrialBalanceRowSchema, data, 'fund trial balance row', { isArray: true })
  } catch (error) {
    throw new Error(`Fund trial balance validation failed: ${error}`)
  }
}

export const FundGlDetailRowSchema = z
  .object({
    acc_journals__id: z.string(),
    acc_journals__journal_date: z.string().nullish(),
    acc_journals__journal_date_fmt: z.string().nullish(),
    acc_journals__description: z.string().nullish(),
    cfg_acc_items__label: z.string().nullish(),
    prog__debit: z.number().nullish(),
    prog__debit_fmt: z.string().nullish(),
    prog__credit: z.number().nullish(),
    prog__credit_fmt: z.string().nullish(),
    prog__running_balance: z.number(),
    prog__running_balance_fmt: z.string().nullish(),
    prog__source_type: z.string().nullish(),
    fund_transactions__id: z.string().nullish(),
    asset_transactions__id: z.string().nullish(),
    journal_entries__id: z.string().nullish(),
    investors__name: z.string().nullish()
  })
  .strict()

export type FundGlDetailRow = z.infer<typeof FundGlDetailRowSchema>

export function validateFundGlDetailRows(data: unknown): FundGlDetailRow[] {
  try {
    return validateWithZodSchema(FundGlDetailRowSchema, data, 'fund GL detail row', { isArray: true })
  } catch (error) {
    throw new Error(`Fund GL detail validation failed: ${error}`)
  }
}
